import { useTableStore } from 'components/TableWithCheck/selectTableStore';
import { HOME_SHOW_KEY_ID } from 'constants/homeApp';
import { useCallback, useMemo, useState } from 'react';
import { adminAPI } from 'services/admin';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

const getFormattedThumb = (thumbnails, preserveUrl = false) =>
  thumbnails
    ?.filter(({ type }) => type === 'SQUARE')
    ?.map(thumb => {
      // If 'external' key exists, the data comes from the home-show API
      if ('external' in thumb) {
        return {
          ...(preserveUrl && thumb.url ? { url: thumb.url } : {}),
          type: thumb.type,
          distributionRegion: 'sa-east-1',
          fileKey: thumb.fileKey,
          external: thumb.external,
        };
      }

      const urlWithoutHTTP = thumb.url.replace('https://', '');
      const cleanURL = urlWithoutHTTP.substring(
        urlWithoutHTTP.indexOf('/') + 1
      );
      const fileKey = cleanURL.substring(cleanURL.indexOf('/') + 1);

      return {
        ...(preserveUrl && thumb.url ? { url: thumb.url } : {}),
        type: thumb.type,
        distributionRegion: 'sa-east-1',
        fileKey,
        external: true,
      };
    });

export const useHomeShow = () => {
  const [loading, setLoading] = useState(true);
  const { setSelectedItemsByListId, selectedItems, updateSelectedItemsByList } =
    useTableStore();

  const maxItemsLimitExeeded = useMemo(
    () => selectedItems?.[HOME_SHOW_KEY_ID]?.length >= 10,
    [selectedItems]
  );

  const getHomeShowData = useCallback(async () => {
    try {
      const data = await adminAPI.get('/admin/home-show');
      setSelectedItemsByListId(
        HOME_SHOW_KEY_ID,
        data
          ?.sort((a, b) => +a.sortNumber - +b.sortNumber)
          .map(i => ({ ...i, id: i.contentId, originalId: i.id }))
        // use originalId to save with id from home-show service
      );
    } catch (error) {
      console.log('Erro ao buscar dados da Home List');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadHomeShow = async () => {
    try {
      const selectedValues = selectedItems[HOME_SHOW_KEY_ID];

      const newLocalPayload = _.cloneDeep(selectedValues).map(
        (selectedValue, index) => {
          const newId = selectedValue.originalId
            ? selectedValue.originalId
            : uuidv4();
          return {
            ...selectedValue,
            id: selectedValue.contentId || selectedValue.id,
            contentId: selectedValue.contentId || selectedValue.id,
            originalId: newId,
            description:
              selectedValue.description || selectedValue.shortDescription,
            contentType: selectedValue.contentType || selectedValue.type,
            type: selectedValue.contentType || selectedValue.type,
            sortNumber: selectedValues.sortNumber || index + 1,
            transitionMiliseconds: selectedValue.transitionMiliseconds || 1500,
            thumbnails: getFormattedThumb(selectedValue.thumbnails, true),
          };
        }
      );

      const remotePayload = _.cloneDeep(newLocalPayload).map(selectedValue => ({
        id: selectedValue.originalId,
        description: selectedValue.description,
        contentId: selectedValue.contentId,
        contentType: selectedValue.contentType,
        sortNumber: selectedValue.sortNumber,
        transitionMiliseconds: selectedValue.transitionMiliseconds,
        thumbnails: _.cloneDeep(selectedValue.thumbnails).map(obj =>
          _.omit(obj, ['url', 'tempURL'])
        ),
      }));

      await adminAPI.put('/admin/home-show', {
        body: remotePayload,
      });

      setSelectedItemsByListId(
        HOME_SHOW_KEY_ID,
        newLocalPayload?.sort((a, b) => +a.sortNumber - +b.sortNumber)
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const resetHomeShowData = useCallback(() => {
    updateSelectedItemsByList(HOME_SHOW_KEY_ID, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    uploadHomeShow,
    getHomeShowData,
    resetHomeShowData,
    maxItemsLimitExeeded,
  };
};
